import React from 'react';
import { createPortal } from 'react-dom';
import { confirmDelete } from '../utils/confirm';
import { useDropdown } from '../hooks/useDropdown';

interface ActionButtonCustomItem {
  label: string;
  onClick: () => void;
  icon?: string;
  className?: string;
}

interface ActionButtonsProps {
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  onToggleStatus?: () => void;
  status?: number | boolean;
  viewTitle?: string;
  editTitle?: string;
  deleteTitle?: string;
  deleteMessage?: string;
  statusTitle?: string;
  customActions?: ActionButtonCustomItem[];
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  onView,
  onEdit,
  onDelete,
  onToggleStatus,
  status,
  viewTitle = 'View',
  editTitle = 'Edit',
  deleteTitle = 'Delete',
  deleteMessage = "You won't be able to revert this!",
  statusTitle = 'Toggle Status',
  customActions = [],
}) => {
  const { isOpen, toggle, close, dropdownRef, triggerRef } = useDropdown();
  const [position, setPosition] = React.useState({ top: 0, left: 0 });

  React.useEffect(() => {
    if (isOpen && triggerRef.current) {
      const updatePosition = () => {
        if (triggerRef.current) {
          const rect = triggerRef.current.getBoundingClientRect();
          setPosition({
            top: rect.bottom + 4,
            left: rect.right - 125
          });
        }
      };
      
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen, triggerRef]);

  const handleDelete = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    close();
    const confirmed = await confirmDelete('Are you sure?', deleteMessage);
    if (confirmed && onDelete) {
      onDelete();
    }
  };

  const handleToggleStatus = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    close();
    if (onToggleStatus) {
      onToggleStatus();
    }
  };

  const handleView = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    close();
    if (onView) {
      onView();
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    close();
    if (onEdit) {
      onEdit();
    }
  };

  const isActive = status === 1 || status === true;
  const hasActions = onView || onEdit || onToggleStatus || onDelete || customActions.length > 0;

  if (!hasActions) {
    return null;
  }

  const dropdownMenu = createPortal(
    <div 
      ref={dropdownRef}
      className="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary fw-semibold fs-7 w-125px py-4" 
      style={{ 
        position: 'fixed', 
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 9999,
        minWidth: '125px',
        backgroundColor: 'white',
        boxShadow: '0 0 50px 0 rgba(82, 63, 105, 0.15)',
        display: isOpen ? 'block' : 'none'
      }}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {onView && (
        <div className="menu-item px-3">
          <a href="#" className="menu-link px-3" onClick={handleView}>
            <i className="fas fa-eye me-2"></i>
            {viewTitle}
          </a>
        </div>
      )}
      {onEdit && (
        <div className="menu-item px-3">
          <a href="#" className="menu-link px-3" onClick={handleEdit}>
            <i className="fas fa-pencil me-2"></i>
            {editTitle}
          </a>
        </div>
      )}
      {customActions.map((action, index) => (
        <div className="menu-item px-3" key={index}>
          <a href="#" className={`menu-link px-3 ${action.className || ''}`} onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            close();
            action.onClick();
          }}>
            {action.icon && <i className={`${action.icon} me-2`}></i>}
            {action.label}
          </a>
        </div>
      ))}
      {onToggleStatus && (
        <div className="menu-item px-3">
          <a href="#" className="menu-link px-3" onClick={handleToggleStatus}>
            <i className={`fas fa-${isActive ? 'ban' : 'check'} me-2`}></i>
            {statusTitle}
          </a>
        </div>
      )}
      {onDelete && (
        <div className="menu-item px-3">
          <a href="#" className="menu-link px-3 text-danger" onClick={handleDelete}>
            <i className="fas fa-trash me-2"></i>
            {deleteTitle}
          </a>
        </div>
      )}
    </div>,
    document.body
  );

  return (
    <div className="position-relative" style={{ display: 'inline-block' }}>
      <button
        ref={triggerRef as React.RefObject<HTMLButtonElement>}
        className="btn btn-sm btn-icon btn-light-primary"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        toggle();
      }}
        title="Actions"
        type="button"
      >
        <i className="fas fa-ellipsis-v"></i>
      </button>
      {dropdownMenu}
    </div>
  );
};

export default ActionButtons;
